import { ContentCopy, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface EnrollmentTokenDialogProps {
  open: boolean;
  onClose: () => void;
  token: string;
  clientName: string;
  onRegenerate?: () => Promise<void>;
}

export function EnrollmentTokenDialog({
  open,
  onClose,
  token,
  clientName,
  onRegenerate,
}: EnrollmentTokenDialogProps) {
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    setRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Token de Inscrição</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use este token para registrar o agente do cliente{" "}
          <strong>{clientName}</strong>. O token expira em 72 horas.
        </Typography>

        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
          Este token só será exibido uma vez. Copie-o agora.
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            value={token}
            slotProps={{ input: { readOnly: true } }}
            size="small"
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: "0.85rem",
              },
            }}
          />
          <Tooltip title={copied ? "Copiado!" : "Copiar"}>
            <IconButton onClick={handleCopy} color={copied ? "success" : "default"}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
          {onRegenerate && (
            <Tooltip title="Gerar novo token">
              <Box>
                <IconButton onClick={handleRegenerate} disabled={regenerating}>
                  <Refresh />
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
